import React, { useEffect, useMemo } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import ContentLoading from "src/Components/Loading/contentLoading";
import { dynamicNotification } from "src/utils/Notifications";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import __ from "src/utils/Translations";
import { PermissionsContextProvider } from "src/utils/Permissions/PermissionsContext";
import useAccountsData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/api/useAccountsData";
import useRechargePlanningSystemData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/api/useRechargePlanningSystemData";
import useAllowedBankIds from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/api/useAllowedBankIds";
import ChargeUpsList from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/ChargeUpsList";
import { ChargeUpsRow } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/ChargeUps.models";
import {
  AccountOwnerOperation,
  CyclicChargeCandidate,
} from "src/Pages/Company/api/rechargePlanningSystem/rechargePlanningSystem.models";

type ChargeUpsProps = Readonly<{
  active?: boolean;
  employeeId: string;
  // eslint-disable-next-line react/no-unused-prop-types
  companyId: string;
}>;

export default function ChargeUps({ active = false, employeeId }: ChargeUpsProps) {
  const queryClient = useQueryClient();

  const {
    data: accounts = [],
    isFetching: accountsLoading,
    error: accountsError,
  } = useAccountsData(employeeId, active);
  const {
    data: rechargePlanningSystemData = [],
    isFetching: rechargePlanningSystemLoading,
    error: rpsError,
  } = useRechargePlanningSystemData(employeeId, active);
  const {
    data: allowedBankIds,
    isFetching: allowedBankIdsLoading,
    error: allowedBankIdsError,
  } = useAllowedBankIds(employeeId, active);

  useEffect(() => {
    if (accountsError) {
      newRelicErrorReport(
        accountsError,
        "ChargeUps/index.tsx - useAccountsData"
      );
      dynamicNotification(
        accountsError.message || __("Nie udało się pobrać kont pracownika."),
        "error"
      );
    }
  }, [accountsError]);

  useEffect(() => {
    if (rpsError) {
      newRelicErrorReport(
        rpsError,
        "ChargeUps/index.tsx - useRechargePlanningSystemData"
      );
      dynamicNotification(
        __(
          "Nie udało się pobrać danych o doładowaniach cyklicznych i resetach."
        ),
        "error"
      );
    }
  }, [rpsError]);

  useEffect(() => {
    if (allowedBankIdsError) {
      newRelicErrorReport(
        allowedBankIdsError,
        "ChargeUps/index.tsx - useAllowedBankIds"
      );
      dynamicNotification(
        allowedBankIdsError.message ||
          __("Nie udało się pobrać kont pracownika."),
        "error"
      );
    }
  }, [allowedBankIdsError]);

  const rows: ChargeUpsRow[] = useMemo(() => {
    const plansByBankId = Object.fromEntries(
      rechargePlanningSystemData.map((plan) => [plan.pointsBankId, plan])
    );
    return accounts
      .filter(
        // Restrict banks to those returned by employee-management
        // /employee-accounts, which applies the AHR bank-visibility filter
        // server-side. When the allow-list has not resolved yet we show
        // nothing rather than leaking every bank.
        (account) => allowedBankIds?.has(account.pointsBank.id) ?? false
      )
      .map((account) => {
        const plan = plansByBankId[account.pointsBank.id] ?? null;
        return {
          ...account,
          cyclicChargeDefinitionId: plan?.cyclicChargeDefinitionId ?? null,
          cyclicChargeCandidate: plan?.cyclicChargeCandidate ?? null,
          reset: plan?.reset ?? null,
        };
      });
  }, [accounts, rechargePlanningSystemData, allowedBankIds]);

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["charge-ups-accounts", employeeId],
    });
  };

  const handleCyclicChargeCandidateUpdated = (pointsBankId: string, cyclicChargeCandidate: CyclicChargeCandidate | null) => {
    queryClient.setQueryData<AccountOwnerOperation[]>(
      ["charge-ups-recharge-planning-system", employeeId],
      (prev) => prev?.map((entry) =>
        entry.pointsBankId === pointsBankId ? { ...entry, cyclicChargeCandidate } : entry
      )
    );
  };

  if (!active) return null;

  return (
    <Card>
      <ContentLoading
        show={
          accountsLoading ||
          rechargePlanningSystemLoading ||
          allowedBankIdsLoading
        }
      >
        <CardHeader>
          <CardTitle>{__("Banki Punktów")}</CardTitle>
        </CardHeader>
        <CardBody>
          <PermissionsContextProvider>
            <ChargeUpsList
              rows={rows}
              employeeId={employeeId}
              onRefresh={handleRefresh}
              onCyclicChargeCandidateUpdated={handleCyclicChargeCandidateUpdated}
            />
          </PermissionsContextProvider>
        </CardBody>
      </ContentLoading>
    </Card>
  );
}
